import { inject, Injectable, isDevMode } from '@angular/core';
import { ClientBaseApi, ConfigService } from '@ash-workspace/core';
import { firstValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class PushNotificationService extends ClientBaseApi {
  private readonly configService = inject(ConfigService);
  private swRegistration: ServiceWorkerRegistration | undefined = undefined;

  private getNotificationsApiUrl(path: string): string {
    const baseUrl = this.configService.getConfig('apiUrl') as string;
    const normalizedBaseUrl = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`;
    const normalizedPath = path.startsWith('/') ? path.slice(1) : path;

    return `${normalizedBaseUrl}${normalizedPath}`;
  }

  private usesApplicationServerKey(
    subscription: PushSubscription,
    expectedKey: Uint8Array,
  ): boolean {
    const currentKey = subscription.options.applicationServerKey;

    if (!currentKey) {
      return false;
    }

    const currentKeyBytes = new Uint8Array(currentKey);

    if (currentKeyBytes.length !== expectedKey.length) {
      return false;
    }

    for (let index = 0; index < currentKeyBytes.length; index += 1) {
      if (currentKeyBytes[index] !== expectedKey[index]) {
        return false;
      }
    }

    return true;
  }

  public async initializePushNotifications(): Promise<void> {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      console.warn('Push-Benachrichtigungen werden nicht unterstützt');
      return;
    }

    if (isDevMode()) {
      console.info('Push-Benachrichtigungen sind im Dev-Modus deaktiviert.');
      return;
    }

    try {
      // Service Worker registrieren falls nicht bereits geschehen
      this.swRegistration = await navigator.serviceWorker.getRegistration();

      if (!this.swRegistration) {
        this.swRegistration = await navigator.serviceWorker.register('/ngsw-worker.js');
      }

      // Für Push muss eine aktive Registrierung vorliegen.
      this.swRegistration = await navigator.serviceWorker.ready;
      if (!this.swRegistration.active) {
        console.warn('Service Worker ist noch nicht aktiv, Push-Initialisierung abgebrochen.');
        return;
      }

      const vapidPublicKey = this.configService.getConfig('vapidPublicKey');
      if (!vapidPublicKey) {
        console.error('VAPID Public Key fehlt in config.json');
        return;
      }

      const applicationServerKey = this.urlBase64ToUint8Array(vapidPublicKey);
      const applicationServerKeyBuffer = new Uint8Array(applicationServerKey).buffer;

      let subscription = await this.swRegistration.pushManager.getSubscription();

      // Wenn sich der VAPID-Key geändert hat, muss die Subscription erneuert werden.
      if (subscription && !this.usesApplicationServerKey(subscription, applicationServerKey)) {
        await subscription.unsubscribe();
        subscription = null;
      }

      if (!subscription) {
        subscription = await this.subscribeToPushNotifications(applicationServerKeyBuffer);
      }

      if (subscription) {
        await this.sendSubscriptionToServer(subscription);
      }

      this.registerPushMessageHandler();
    } catch (error) {
      console.error('Fehler beim Initialisieren von Push-Benachrichtigungen:', error);
    }
  }

  private async subscribeToPushNotifications(
    applicationServerKey: BufferSource,
  ): Promise<PushSubscription | null> {
    if (!this.swRegistration) {
      console.error('Service Worker nicht registriert');
      return null;
    }

    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        console.log('Benachrichtigungsberechtigung verweigert');
        return null;
      }

      const subscription = await this.swRegistration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey,
      });

      return subscription;
    } catch (error) {
      console.error('Fehler beim Abonnieren von Push-Benachrichtigungen:', error);
      return null;
    }
  }

  private async sendSubscriptionToServer(subscription: PushSubscription): Promise<void> {
    try {
      await firstValueFrom(
        this.postData(this.getNotificationsApiUrl('notifications/subscribe'), {
          subscription: subscription.toJSON(),
        }),
      );

      console.log('Push-Subscription zum Server gesendet');
    } catch (error) {
      console.error('Fehler beim Senden der Subscription zum Server:', error);
    }
  }

  /**
   * Registriert einen Handler für Push-Messages
   */
  private registerPushMessageHandler(): void {
    if (!navigator.serviceWorker.controller) {
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        this.registerPushMessageHandler();
      });
      return;
    }

    navigator.serviceWorker.controller.postMessage({
      type: 'REGISTER_PUSH_HANDLER',
    });
  }

  /**
   * Unsubscribe von Push-Benachrichtigungen
   */
  public async unsubscribePushNotifications(): Promise<void> {
    if (!this.swRegistration) return;

    try {
      const subscription = await this.swRegistration.pushManager.getSubscription();
      if (subscription) {
        await subscription.unsubscribe();

        // Server benachrichtigen
        await firstValueFrom(
          this.postData(this.getNotificationsApiUrl('notifications/unsubscribe'), {
            subscription: subscription.toJSON(),
          }),
        );

        console.log('Push-Benachrichtigungen deaktiviert');
      }
    } catch (error) {
      console.error('Fehler beim Deaktivieren von Push-Benachrichtigungen:', error);
    }
  }

  /**
   * Prüft ob Push-Benachrichtigungen aktiviert sind
   */
  public async isPushNotificationsEnabled(): Promise<boolean> {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      return false;
    }

    try {
      const registration = await navigator.serviceWorker.getRegistration();
      if (!registration) return false;

      const subscription = await registration.pushManager.getSubscription();
      return !!subscription;
    } catch (error) {
      console.error('Fehler beim Prüfen von Push-Benachrichtigungen:', error);
      return false;
    }
  }

  private urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }

    return outputArray;
  }
}
